import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import LegalLayout from '../components/LegalLayout';

const PROVIDER = {
  name: 'HR Aristu, SLU',
  cif: 'B71445308',
  address: 'Calle San Nicolás 68-70, 6º, 31001 Pamplona (Navarra), España',
  email: 'soporte@hraristu.com'
};

const SECTIONS_ES = [
  {
    heading: '1. Responsable del tratamiento',
    body: [
      `${PROVIDER.name}, con CIF ${PROVIDER.cif} y domicilio en ${PROVIDER.address}, es el Responsable del Tratamiento de los datos personales descritos en esta política. Puedes contactarnos para cualquier cuestión sobre privacidad en ${PROVIDER.email}. No disponemos de Delegado de Protección de Datos por no ser exigible dado el tamaño y actividad de la empresa.`
    ]
  },
  {
    heading: '2. Dos roles distintos: tu cuenta y los datos de tus trabajadores',
    body: [
      'Esta política cubre los datos personales que tratamos como Responsables: los tuyos y los de las personas de contacto de tu empresa (nombre, email, contraseña, datos de facturación).',
      'Los datos de los trabajadores que tú (como Cliente) introduces en Regulisto para gestionar su jornada laboral (fichajes, geolocalización, turnos, vacaciones, ausencias, PIN de fichaje) los tratamos como Encargados del Tratamiento, por cuenta y siguiendo instrucciones de tu empresa, que es la Responsable frente a esos trabajadores. Si eres empleado de una empresa que usa Regulisto, debes dirigir tus consultas sobre esos datos a tu empresa (empleadora).'
    ]
  },
  {
    heading: '3. Datos que recogemos',
    body: [
      {
        list: [
          'Datos de registro: nombre de la empresa, nombre y email del administrador, contraseña (almacenada siempre cifrada, nunca en texto plano).',
          'Datos de facturación: gestionados directamente por Stripe, Inc.; nosotros no almacenamos los datos completos de tu tarjeta de pago.',
          'Datos operativos introducidos por tu empresa: fichajes (fecha, hora, tipo), ubicación GPS del fichaje (si la geolocalización está activada), turnos, vacaciones, ausencias, incidencias y documentos asociados.',
          'Datos técnicos: dirección IP y registros de acceso por motivos de seguridad, token de sesión almacenado en el navegador (localStorage) para mantenerte identificado.'
        ]
      }
    ]
  },
  {
    heading: '4. Finalidades y base legal',
    body: [
      {
        list: [
          'Prestar el Servicio contratado y gestionar tu cuenta y la prueba gratuita — ejecución del contrato (art. 6.1.b RGPD).',
          'Gestionar el cobro de la suscripción a través de Stripe — ejecución del contrato y cumplimiento de obligaciones fiscales (art. 6.1.b y 6.1.c RGPD).',
          'Enviar comunicaciones de servicio (confirmación de cuenta, avisos de facturación, incidencias) — ejecución del contrato e interés legítimo.',
          'Validar el radio de ubicación permitido en los fichajes con geolocalización — ejecución del contrato, según la configuración que tu empresa active.',
          'Mejorar la seguridad del Servicio (protección contra accesos no autorizados) — interés legítimo.'
        ]
      }
    ]
  },
  {
    heading: '5. Encargados y destinatarios',
    body: [
      'Para prestar el Servicio recurrimos a los siguientes encargados del tratamiento, con quienes tenemos suscritos los contratos exigidos por el artículo 28 RGPD:',
      {
        list: [
          'Stripe, Inc. — procesamiento de pagos y facturación.',
          'Proveedor de alojamiento (VPS/infraestructura Coolify) — hospedaje de la aplicación y la base de datos.',
          'Proveedor de correo SMTP — envío de emails transaccionales (confirmación de cuenta, recuperación de contraseña, notificaciones).'
        ]
      },
      'Algunos de estos proveedores pueden estar ubicados fuera del Espacio Económico Europeo; en ese caso, el tratamiento se ampara en las Cláusulas Contractuales Tipo de la Comisión Europea u otra garantía adecuada conforme al Capítulo V del RGPD.'
    ]
  },
  {
    heading: '6. Conservación',
    body: [
      'Conservamos los datos de tu cuenta mientras mantengas una relación contractual con nosotros. Si finaliza el periodo de prueba sin suscripción, o cancelas tu suscripción, conservamos los datos durante un plazo razonable por si decides reactivar la cuenta, y posteriormente los eliminamos o anonimizamos, salvo obligación legal de conservación superior (por ejemplo, la normativa laboral exige conservar los registros de jornada durante 4 años).'
    ]
  },
  {
    heading: '7. Seguridad',
    body: [
      {
        list: [
          'Contraseñas almacenadas con hash bcrypt, nunca en texto plano.',
          'Comunicaciones cifradas mediante HTTPS/TLS.',
          'Sesiones basadas en tokens JWT con expiración y revocación en el cierre de sesión.',
          'Aislamiento estricto de datos entre empresas clientes (arquitectura multi-tenant).',
          'Hash de verificación y firma digital en los registros de fichaje para garantizar su integridad.'
        ]
      }
    ]
  },
  {
    heading: '8. Tus derechos',
    body: [
      'Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad escribiendo a ' + PROVIDER.email + '. Responderemos en el plazo legalmente previsto. También tienes derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es) si consideras que el tratamiento no se ajusta a la normativa.',
      'Si eres trabajador de una empresa que usa Regulisto y quieres ejercer tus derechos sobre tus datos de fichaje, debes dirigirte primero a tu empresa, que es la Responsable de ese tratamiento.'
    ]
  },
  {
    heading: '9. Cookies y almacenamiento local',
    body: [
      'Regulisto no utiliza cookies de seguimiento ni de publicidad. Utilizamos el almacenamiento local del navegador (localStorage) exclusivamente para guardar el token de sesión que te mantiene identificado mientras usas la aplicación; este dato no se comparte con terceros y se borra al cerrar sesión.'
    ]
  },
  {
    heading: '10. Menores de edad',
    body: [
      'El Servicio está dirigido a empresas y profesionales; no está destinado a menores de edad.'
    ]
  },
  {
    heading: '11. Cambios en esta política',
    body: [
      'Podemos actualizar esta Política de Privacidad para reflejar cambios legales o del Servicio. Los cambios sustanciales se comunicarán mediante email o aviso en la aplicación.'
    ]
  }
];

const SECTIONS_EN = [
  {
    heading: '1. Data controller',
    body: [
      `${PROVIDER.name}, tax ID (CIF) ${PROVIDER.cif}, registered at ${PROVIDER.address}, Spain, is the Data Controller for the personal data described in this policy. You can reach us for any privacy question at ${PROVIDER.email}. We do not have a Data Protection Officer, as it is not required given the size and activity of the company.`
    ]
  },
  {
    heading: '2. Two different roles: your account vs. your employees\' data',
    body: [
      'This policy covers the personal data we process as Controller: yours and your company\'s contacts (name, email, password, billing information).',
      'Data about employees that you (the Customer) enter into Regulisto to manage their working time (clock-ins, geolocation, shifts, vacations, absences, clock-in PIN) is processed by us as Processor, on behalf of and under the instructions of your company, which is the Controller with respect to those employees. If you are an employee of a company using Regulisto, you should direct any request about that data to your employer.'
    ]
  },
  {
    heading: '3. Data we collect',
    body: [
      {
        list: [
          'Signup data: company name, administrator name and email, password (always stored hashed, never in plain text).',
          'Billing data: handled directly by Stripe, Inc.; we never store your full payment card details.',
          'Operational data entered by your company: clock-ins (date, time, type), GPS location of the clock-in (if geolocation is enabled), shifts, vacations, absences, incidents, and related documents.',
          'Technical data: IP address and access logs for security purposes, and a session token stored in your browser (localStorage) to keep you signed in.'
        ]
      }
    ]
  },
  {
    heading: '4. Purposes and legal basis',
    body: [
      {
        list: [
          'Provide the contracted Service and manage your account and free trial — performance of a contract (Art. 6.1.b GDPR).',
          'Manage subscription billing via Stripe — performance of a contract and compliance with tax obligations (Art. 6.1.b and 6.1.c GDPR).',
          'Send service communications (account confirmation, billing notices, incident alerts) — performance of a contract and legitimate interest.',
          'Validate the allowed location radius for geolocation-based clock-ins — performance of a contract, based on your company\'s configuration.',
          'Improve Service security (protection against unauthorized access) — legitimate interest.'
        ]
      }
    ]
  },
  {
    heading: '5. Processors and recipients',
    body: [
      'To provide the Service we rely on the following processors, with whom we have the data processing agreements required by Article 28 GDPR:',
      {
        list: [
          'Stripe, Inc. — payment processing and billing.',
          'Hosting provider (VPS/Coolify infrastructure) — hosting the application and database.',
          'SMTP email provider — sending transactional emails (account confirmation, password recovery, notifications).'
        ]
      },
      'Some of these providers may be located outside the European Economic Area; in that case, the transfer is covered by the European Commission\'s Standard Contractual Clauses or another appropriate safeguard under Chapter V GDPR.'
    ]
  },
  {
    heading: '6. Data retention',
    body: [
      'We retain your account data for as long as you maintain a contractual relationship with us. If the trial ends without a subscription, or you cancel, we retain the data for a reasonable period in case you reactivate your account, after which it is deleted or anonymized, unless a longer legal retention period applies (for example, Spanish labor law requires keeping time-tracking records for 4 years).'
    ]
  },
  {
    heading: '7. Security',
    body: [
      {
        list: [
          'Passwords stored with bcrypt hashing, never in plain text.',
          'Encrypted communications via HTTPS/TLS.',
          'JWT-based sessions with expiration and revocation on logout.',
          'Strict data isolation between customer companies (multi-tenant architecture).',
          'Verification hash and digital signature on clock-in records to guarantee integrity.'
        ]
      }
    ]
  },
  {
    heading: '8. Your rights',
    body: [
      `You can exercise your rights of access, rectification, erasure, objection, restriction of processing, and portability by writing to ${PROVIDER.email}. We will respond within the legally required timeframe. You also have the right to file a complaint with the Spanish Data Protection Agency (www.aepd.es) if you believe the processing does not comply with the law.`,
      'If you are an employee of a company using Regulisto and want to exercise your rights over your clock-in data, you should first contact your employer, which is the Controller for that processing.'
    ]
  },
  {
    heading: '9. Cookies and local storage',
    body: [
      'Regulisto does not use tracking or advertising cookies. We use browser local storage (localStorage) solely to keep the session token that identifies you while using the app; this data is never shared with third parties and is cleared on logout.'
    ]
  },
  {
    heading: '10. Minors',
    body: [
      'The Service is intended for businesses and professionals; it is not directed at minors.'
    ]
  },
  {
    heading: '11. Changes to this policy',
    body: [
      'We may update this Privacy Policy to reflect legal or Service changes. Material changes will be communicated via email or an in-app notice.'
    ]
  }
];

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';

  return (
    <LegalLayout
      title={isEs ? 'Política de Privacidad' : 'Privacy Policy'}
      effectiveDate={isEs ? '23 de agosto de 2026' : 'August 23, 2026'}
      sections={isEs ? SECTIONS_ES : SECTIONS_EN}
    />
  );
};

export default PrivacyPolicy;
